// Presentation helpers for shop catalogue data. Pure functions, no I/O.

import type { ShopProduct, ShopVariant } from './index'
import { LOCALES, type Lang } from '../../config/locales'

/**
 * Formats a decimal price string (e.g. "224.10") as a DKK amount with the
 * "kr" suffix. Whole amounts render without decimals; fractional amounts keep
 * two. Locale controls the decimal/thousands separators (da-DK uses a comma).
 */
export function formatKr(amount: string, locale: Lang = 'en'): string {
  const n = Number(amount)
  if (!Number.isFinite(n)) return `${amount} kr`
  const hasFraction = Math.round(n * 100) % 100 !== 0
  const formatted = new Intl.NumberFormat(LOCALES[locale].intl, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n)
  return `${formatted} kr`
}

/**
 * Returns the cheapest variant of a product — the price surfaced on cards and
 * as the default selection. Falls back to a synthetic zero-price variant when a
 * product has no variants (defensive; the API always returns at least one).
 */
export function lowestVariant(product: ShopProduct): ShopVariant {
  const [first, ...rest] = product.variants
  if (!first) {
    return {
      id: 0,
      title: '',
      price: '0',
      compareAtPrice: null,
      availableForSale: false,
    }
  }
  return rest.reduce((min, v) => (Number(v.price) < Number(min.price) ? v : min), first)
}

/**
 * Whole-number discount percentage between a compare-at price and the current
 * price. Returns null when there is no genuine markdown.
 */
export function discountPercent(price: string, compareAtPrice: string | null): number | null {
  if (!compareAtPrice) return null
  const now = Number(price)
  const was = Number(compareAtPrice)
  if (!Number.isFinite(now) || !Number.isFinite(was) || was <= now) return null
  return Math.round(((was - now) / was) * 100)
}
